/* eslint-disable typebox-snake-case/typebox-object-keys-snake-case -- camelCase keys match existing DB/API schema */
import { Static, Type } from '@sinclair/typebox';

export const WebsiteRadiusStyleSchema = Type.Union([
  Type.Literal('none'),
  Type.Literal('soft'),
  Type.Literal('rounded'),
]);
export type WebsiteRadiusStyle = Static<typeof WebsiteRadiusStyleSchema>;

export const WebsiteVerticalSpacingSchema = Type.Union([
  Type.Literal('tight'),
  Type.Literal('standard'),
  Type.Literal('airy'),
]);
export type WebsiteVerticalSpacing = Static<typeof WebsiteVerticalSpacingSchema>;

export const WebsiteDecorationStyleSchema = Type.Union([
  Type.Literal('none'),
  Type.Literal('dots'),
  Type.Literal('curves'),
  Type.Literal('diamonds'),
  Type.Literal('waves'),
  Type.Literal('lines'),
  Type.Literal('crosses'),
]);
export type WebsiteDecorationStyle = Static<typeof WebsiteDecorationStyleSchema>;

export const WebsiteColorsSchema = Type.Object({
  background: Type.String(),
  accent: Type.String(),
  text: Type.String(),
  cta: Type.String(),
});
export type WebsiteColors = Static<typeof WebsiteColorsSchema>;

export const WebsiteTypographiesSchema = Type.Object({
  body: Type.String(),
  heading: Type.String(),
});
export type WebsiteTypographies = Static<typeof WebsiteTypographiesSchema>;

export const WebsiteStylesSchema = Type.Object({
  colors: WebsiteColorsSchema,
  typography: WebsiteTypographiesSchema,
  borderStyle: WebsiteRadiusStyleSchema,
  decoration: Type.Optional(WebsiteDecorationStyleSchema),
  // Espacement vertical entre les composants (défaut: 'standard'). Voir getThemeVariableCss.
  verticalSpacing: Type.Optional(WebsiteVerticalSpacingSchema),
});
export type WebsiteStyles = Static<typeof WebsiteStylesSchema>;

export enum GoogleFontFamily {
  Inter = 'Inter',
  Poppins = 'Poppins',
  Lato = 'Lato',
  Roboto = 'Roboto',
  OpenSans = 'Open Sans',
  Montserrat = 'Montserrat',
  Playfair = 'Playfair Display',
  Raleway = 'Raleway',
  SourceSansPro = 'Source Sans Pro',
  Nunito = 'Nunito',
  Merriweather = 'Merriweather',
}

export interface WebsitePalette {
  name: string;
  colors: WebsiteColors;
  /** Palette premium (gating Volet B — feature BUILDER_PREMIUM, plan PRO+). cf. isPremiumPaletteBackground. */
  premium?: boolean;
}

export const WEBSITE_PALETTES: WebsitePalette[] = [
  /* ── Light (gratuites, tous plans) ── */
  { name: 'Classique', colors: { background: '#ffffff', text: '#1a1a1a', cta: '#1a1a1a', accent: '#444444' } },
  { name: 'Océan', colors: { background: '#f8fafc', text: '#0f172a', cta: '#0369a1', accent: '#0284c7' } },
  { name: 'Forêt', colors: { background: '#fafaf9', text: '#1c1917', cta: '#15803d', accent: '#16a34a' } },
  { name: 'Lavande', colors: { background: '#faf5ff', text: '#1e1b4b', cta: '#7c3aed', accent: '#8b5cf6' } },
  { name: 'Corail', colors: { background: '#fff7ed', text: '#1c1917', cta: '#ea580c', accent: '#f97316' } },
  { name: 'Rose', colors: { background: '#fdf2f8', text: '#1c1917', cta: '#db2777', accent: '#ec4899' } },
  /* ── Dark (premium) ── */
  {
    name: 'Nuit',
    premium: true,
    colors: { background: '#0f172a', text: '#f1f5f9', cta: '#e2e8f0', accent: '#94a3b8' },
  },
  {
    name: 'Charbon',
    premium: true,
    colors: { background: '#18181b', text: '#fafafa', cta: '#f4f4f5', accent: '#a1a1aa' },
  },
  {
    name: 'Émeraude',
    premium: true,
    colors: { background: '#022c22', text: '#ecfdf5', cta: '#34d399', accent: '#6ee7b7' },
  },
  {
    name: 'Crépuscule',
    premium: true,
    colors: { background: '#1e1b4b', text: '#e0e7ff', cta: '#818cf8', accent: '#a5b4fc' },
  },
  /* ── Warm / earthy (premium) ── */
  {
    name: 'Sable',
    premium: true,
    colors: { background: '#fefce8', text: '#422006', cta: '#ca8a04', accent: '#eab308' },
  },
  {
    name: 'Terracotta',
    premium: true,
    colors: { background: '#fef2f2', text: '#1c1917', cta: '#b91c1c', accent: '#dc2626' },
  },
];

/** Palette de repli (toujours gratuite) servie quand un downgrade retire l'entitlement premium. */
export const FALLBACK_PALETTE_NAME = 'Classique';

/** Une couleur de fond correspond-elle à une palette premium ? (neutralisation au rendu, B-P1) */
export function isPremiumPaletteBackground(background: string | undefined | null): boolean {
  if (!background) return false;
  const normalized = background.trim().toLowerCase();
  return WEBSITE_PALETTES.some(
    (p: WebsitePalette): boolean => p.premium === true && p.colors.background.toLowerCase() === normalized,
  );
}

/* ── Color contrast utilities (WCAG) ── */

/** WCAG relative luminance — returns 0 (black) … 1 (white) */
export const hexToLuminance = (hex: string): number => {
  const cleaned = hex.replace('#', '');
  const full =
    cleaned.length === 3
      ? cleaned
          .split('')
          .map((c: string): string => c + c)
          .join('')
      : cleaned;
  const int = parseInt(full, 16);
  const srgb = [int >> 16, (int >> 8) & 255, int & 255].map((c: number): number => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
};

/** WCAG contrast ratio between two hex colors */
export const contrastRatio = (hex1: string, hex2: string): number => {
  const l1 = hexToLuminance(hex1);
  const l2 = hexToLuminance(hex2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

export const GoogleFontMap: Record<GoogleFontFamily, string> = {
  [GoogleFontFamily.Inter]: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap',
  [GoogleFontFamily.Poppins]: 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&display=swap',
  [GoogleFontFamily.Lato]: 'https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap',
  [GoogleFontFamily.Roboto]: 'https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap',
  [GoogleFontFamily.OpenSans]: 'https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&display=swap',
  [GoogleFontFamily.Montserrat]: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap',
  [GoogleFontFamily.Playfair]: 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&display=swap',
  [GoogleFontFamily.Raleway]: 'https://fonts.googleapis.com/css2?family=Raleway:wght@400;600;700&display=swap',
  [GoogleFontFamily.SourceSansPro]:
    'https://fonts.googleapis.com/css2?family=Source+Sans+Pro:wght@400;600;700&display=swap',
  [GoogleFontFamily.Nunito]: 'https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700&display=swap',
  [GoogleFontFamily.Merriweather]: 'https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700&display=swap',
};
